import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const MemoryApis = ({ onSectionChange }) => {
  return (
    <div className="doc-content">
      <div className="api-header-section">
        <div className="api-header-content">
          <div className="api-category-badge">
            <i className="fa-solid fa-code"></i>
            <span>API Reference</span>
          </div>
          <h1>Memory APIs</h1>
          <p className="api-header-description">
            MemU Memory APIs provide direct control over memory operations, allowing you to manually manage
            conversation storage and retrieval for advanced use cases.
          </p>
        </div>
        <div className="api-header-icon">
          <i className="fa-solid fa-database"></i>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="endpoints-overview">Endpoints Overview</h2>
        <div className="api-overview-table">
          <table>
            <thead>
              <tr>
                <th>Method</th>
                <th>Endpoint</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><span className="http-method post">POST</span></td>
                <td><code>/api/v1/memory/memorize</code></td>
                <td>Register a memorization task</td>
              </tr>
              <tr>
                <td><span className="http-method get">GET</span></td>
                <td><code>/api/v1/memory/memorize/status/&#123;task_id&#125;</code></td>
                <td>Get memorization task status</td>
              </tr>
              <tr>
                <td><span className="http-method post">POST</span></td>
                <td><code>/api/v1/memory/delete</code></td>
                <td>Delete user or agent memories</td>
              </tr>
              <tr>
                <td><span className="http-method post">POST</span></td>
                <td><code>/api/v1/memory/retrieve/default-categories</code></td>
                <td>Retrieve default categories</td>
              </tr>
              <tr>
                <td><span className="http-method post">POST</span></td>
                <td><code>/api/v1/memory/retrieve/related-memory-items</code></td>
                <td>Retrieve related memory items</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="authentication">Authentication</h2>
        <p>All Memory API requests require authentication using your API key. Include your API key in the request headers:</p>
        
        <div className="code-block">
          <SyntaxHighlighter
            language="bash"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
            {`curl -X POST https://api.memu.so/api/v1/memory/memorize \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`}
          </SyntaxHighlighter>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="base-url">Base URL</h2>
        <p>All API requests should be made to:</p>
        <div className="code-block">
          <SyntaxHighlighter
            language="text"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
            https://api.memu.so/api/v1
          </SyntaxHighlighter>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="memorize-conversation">Memorize Conversation</h2>
        <p>Register a memorization task via Temporal scheduler with global pending limiter.</p>
        
        <div className="api-endpoint">
          <span className="http-method post">POST</span>
          <span className="endpoint-url">/api/v1/memory/memorize</span>
        </div>

        <div className="feature-highlight-box">
          <h4>🧠 Memory Processing</h4>
          <ul>
            <li><strong>Asynchronous Processing:</strong> Tasks are scheduled and processed via Temporal</li>
            <li><strong>Intelligent Categorization:</strong> Automatically organizes into profiles, events, activities, preferences</li>
            <li><strong>Smart Operations:</strong> Determines whether to ADD, UPDATE, DELETE, or TOUCH existing memories</li>
            <li><strong>Vector-Based Linking:</strong> Creates semantic connections between related memories</li>
          </ul>
        </div>

        <h3>Request Body</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`{
  "conversation_text": "User: I love playing tennis on weekends\\nAssistant: That's great!",
  "conversation": [
    {
      "role": "user",
      "name": "John",
      "time": "2024-01-15T10:30:00Z",
      "content": "I love playing tennis on weekends"
    },
    {
      "role": "assistant",
      "name": "Coach",
      "time": "2024-01-15T10:30:15Z",
      "content": "That's great! Tennis is an excellent way to stay active."
    }
  ],
  "user_id": "user_123",
  "user_name": "John Doe",
  "agent_id": "agent_456",
  "agent_name": "Tennis Coach AI",
  "session_date": "2024-01-15"
}`}
          </SyntaxHighlighter>
        </div>

        <h3>Parameters</h3>
        <div className="parameter-table">
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>conversation_text</code></td>
                <td>string</td>
                <td>No</td>
                <td>Plain text representation of the conversation</td>
              </tr>
              <tr>
                <td><code>conversation</code></td>
                <td>array</td>
                <td>No</td>
                <td>Structured conversation with role, name, time, and content</td>
              </tr>
              <tr>
                <td><code>user_id</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Unique identifier for the user</td>
              </tr>
              <tr>
                <td><code>user_name</code></td>
                <td>string</td>
                <td>No</td>
                <td>Display name for the user</td>
              </tr>
              <tr>
                <td><code>agent_id</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Unique identifier for the AI agent</td>
              </tr>
              <tr>
                <td><code>agent_name</code></td>
                <td>string</td>
                <td>No</td>
                <td>Display name for the AI agent</td>
              </tr>
              <tr>
                <td><code>session_date</code></td>
                <td>string</td>
                <td>No</td>
                <td>Date of the conversation session</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>Response (200 OK)</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`{
  "task_id": "task_789abc",
  "status": "pending",
  "message": "Memorization task registered successfully"
}`}
          </SyntaxHighlighter>
        </div>

        <h3>Example Request</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="bash"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`curl -X POST https://api.memu.so/api/v1/memory/memorize \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "conversation": [
      {
        "role": "user",
        "content": "I love playing tennis on weekends"
      },
      {
        "role": "assistant",
        "content": "That's great! Tennis is excellent exercise."
      }
    ],
    "user_id": "user_123",
    "user_name": "John Doe",
    "agent_id": "agent_456",
    "agent_name": "Tennis Coach AI"
  }'`}
          </SyntaxHighlighter>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="get-task-status">Get Memorization Task Status</h2>
        <p>Get the status of a memorization task.</p>
        
        <div className="api-endpoint">
          <span className="http-method get">GET</span>
          <span className="endpoint-url">/api/v1/memory/memorize/status/&#123;task_id&#125;</span>
        </div>

        <h3>Path Parameters</h3>
        <div className="parameter-table">
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>task_id</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Task ID returned from memorize endpoint</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>Response (200 OK)</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`"completed"`}
          </SyntaxHighlighter>
        </div>

        <h3>Status Values</h3>
        <div className="status-list">
          <div className="status-item">
            <span className="status-badge processing">pending</span>
            <span>Task is waiting to be processed</span>
          </div>
          <div className="status-item">
            <span className="status-badge processing">processing</span>
            <span>Task is being processed</span>
          </div>
          <div className="status-item">
            <span className="status-badge completed">completed</span>
            <span>Task completed successfully</span>
          </div>
          <div className="status-item">
            <span className="status-badge failed">failed</span>
            <span>Task failed with error</span>
          </div>
        </div>

        <h3>Example Request</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="bash"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`curl -X GET https://api.memu.so/api/v1/memory/memorize/status/task_789abc \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
          </SyntaxHighlighter>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="delete-memory">Delete Memories</h2>
        <p>Delete memories for a given user. If agent_id is provided, delete only that agent's memories; otherwise delete all memories for the user within the project.</p>
        
        <div className="api-endpoint">
          <span className="http-method post">POST</span>
          <span className="endpoint-url">/api/v1/memory/delete</span>
        </div>

        <div className="feature-highlight-box">
          <h4>🗑️ Memory Deletion</h4>
          <ul>
            <li><strong>User-Level Deletion:</strong> Delete all memories for a user across all agents</li>
            <li><strong>Agent-Level Deletion:</strong> Delete only memories for a specific user-agent pair</li>
            <li><strong>Immediate Effect:</strong> Changes take effect instantly</li>
            <li><strong>Confirmation:</strong> Returns status message for verification</li>
          </ul>
        </div>

        <h3>Request Body</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`{
  "user_id": "user_123",
  "agent_id": "agent_456"
}`}
          </SyntaxHighlighter>
        </div>

        <h3>Parameters</h3>
        <div className="parameter-table">
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>user_id</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Unique identifier for the user</td>
              </tr>
              <tr>
                <td><code>agent_id</code></td>
                <td>string</td>
                <td>No</td>
                <td>If provided, delete only this agent's memories; otherwise delete all user memories</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>Response (200 OK)</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`"Memories deleted successfully"`}
          </SyntaxHighlighter>
        </div>

        <h3>Example Request</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="bash"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`curl -X POST https://api.memu.so/api/v1/memory/delete \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "user_123",
    "agent_id": "agent_456"
  }'`}
          </SyntaxHighlighter>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="retrieve-categories">Retrieve Default Categories</h2>
        <p>Retrieve default categories and their content for a project. Supports both memory items and summaries.</p>
        
        <div className="api-endpoint">
          <span className="http-method post">POST</span>
          <span className="endpoint-url">/api/v1/memory/retrieve/default-categories</span>
        </div>

        <div className="feature-highlight-box">
          <h4>📂 Category Retrieval</h4>
          <ul>
            <li><strong>Flexible Format:</strong> Choose between raw memory items or summaries</li>
            <li><strong>Structured Data:</strong> Returns organized categories with metadata</li>
            <li><strong>Full Context:</strong> Includes memory count and timestamps</li>
            <li><strong>User & Agent Scoped:</strong> Filter by specific user-agent combinations</li>
          </ul>
        </div>

        <h3>Request Body</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`{
  "user_id": "user_123",
  "agent_id": "agent_456",
  "want_memory_items": true
}`}
          </SyntaxHighlighter>
        </div>

        <h3>Parameters</h3>
        <div className="parameter-table">
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>user_id</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Unique identifier for the user</td>
              </tr>
              <tr>
                <td><code>agent_id</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Unique identifier for the AI agent</td>
              </tr>
              <tr>
                <td><code>want_memory_items</code></td>
                <td>boolean</td>
                <td>No</td>
                <td>If true, return raw memory items; if false, return summaries (default: false)</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>Response (200 OK)</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`{
  "categories": [
    {
      "name": "activities",
      "type": "default",
      "user_id": "user_123",
      "agent_id": "agent_456",
      "description": "User activities and hobbies",
      "memory_items": {
        "memories": [
          {
            "memory_id": "mem_123",
            "category": "activities",
            "content": "John enjoys playing tennis on weekends",
            "happened_at": "2024-01-15T10:30:00Z",
            "created_at": "2024-01-15T10:35:00Z",
            "updated_at": "2024-01-15T10:35:00Z"
          }
        ],
        "memory_count": 1
      },
      "summary": "User enjoys outdoor sports, particularly tennis"
    }
  ],
  "total_categories": 1
}`}
          </SyntaxHighlighter>
        </div>

        <h3>Example Request</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="bash"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`curl -X POST https://api.memu.so/api/v1/memory/retrieve/default-categories \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "user_123",
    "agent_id": "agent_456",
    "want_memory_items": true
  }'`}
          </SyntaxHighlighter>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="retrieve-related">Retrieve Related Memory Items</h2>
        <p>Retrieve related memory items from activities using embedding search with similarity scoring.</p>
        
        <div className="api-endpoint">
          <span className="http-method post">POST</span>
          <span className="endpoint-url">/api/v1/memory/retrieve/related-memory-items</span>
        </div>

        <div className="feature-highlight-box">
          <h4>🔍 Smart Retrieval</h4>
          <ul>
            <li><strong>Semantic Search:</strong> Find memories by meaning using embedding similarity</li>
            <li><strong>Relevance Scoring:</strong> Results ranked by similarity score (0-1)</li>
            <li><strong>Category Filtering:</strong> Optionally filter by specific categories</li>
            <li><strong>Configurable Results:</strong> Control top_k and minimum similarity threshold</li>
          </ul>
        </div>

        <h3>Request Body</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`{
  "user_id": "user_123",
  "agent_id": "agent_456",
  "query": "What sports does the user enjoy?",
  "top_k": 10,
  "min_similarity": 0.3,
  "include_categories": ["activities", "preferences"]
}`}
          </SyntaxHighlighter>
        </div>

        <h3>Parameters</h3>
        <div className="parameter-table">
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>user_id</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Unique identifier for the user</td>
              </tr>
              <tr>
                <td><code>agent_id</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Unique identifier for the AI agent</td>
              </tr>
              <tr>
                <td><code>query</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Search query to find relevant memories</td>
              </tr>
              <tr>
                <td><code>top_k</code></td>
                <td>integer</td>
                <td>No</td>
                <td>Maximum number of results to return (default: 10)</td>
              </tr>
              <tr>
                <td><code>min_similarity</code></td>
                <td>float</td>
                <td>No</td>
                <td>Minimum similarity score threshold (default: 0.3)</td>
              </tr>
              <tr>
                <td><code>include_categories</code></td>
                <td>array</td>
                <td>No</td>
                <td>Filter by specific category names</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>Response (200 OK)</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`{
  "related_memories": [
    {
      "memory": {
        "memory_id": "mem_123",
        "category": "activities",
        "content": "John enjoys playing tennis on weekends",
        "happened_at": "2024-01-15T10:30:00Z",
        "created_at": "2024-01-15T10:35:00Z",
        "updated_at": "2024-01-15T10:35:00Z"
      },
      "user_id": "user_123",
      "agent_id": "agent_456",
      "similarity_score": 0.95
    }
  ],
  "query": "What sports does the user enjoy?",
  "total_found": 1,
  "search_params": {
    "top_k": 10,
    "min_similarity": 0.3
  }
}`}
          </SyntaxHighlighter>
        </div>

        <h3>Example Request</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="bash"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`curl -X POST https://api.memu.so/api/v1/memory/retrieve/related-memory-items \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "user_123",
    "agent_id": "agent_456",
    "query": "What sports does the user enjoy?",
    "top_k": 10,
    "min_similarity": 0.3,
    "include_categories": ["activities"]
  }'`}
          </SyntaxHighlighter>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="error-handling">Error Handling</h2>
        <p>The Memory API uses standard HTTP status codes and returns detailed error messages to help you troubleshoot issues.</p>

        <h3>Common Error Codes</h3>
        <div className="api-overview-table">
          <table>
            <thead>
              <tr>
                <th>Status Code</th>
                <th>Error Type</th>
                <th>Description</th>
                <th>Solution</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>400</code></td>
                <td>Bad Request</td>
                <td>Invalid request parameters</td>
                <td>Check request body structure and required fields</td>
              </tr>
              <tr>
                <td><code>401</code></td>
                <td>Unauthorized</td>
                <td>Missing or invalid API key</td>
                <td>Verify your API key in Authorization header</td>
              </tr>
              <tr>
                <td><code>404</code></td>
                <td>Not Found</td>
                <td>Resource not found</td>
                <td>Check task_id or user_id exists</td>
              </tr>
              <tr>
                <td><code>422</code></td>
                <td>Validation Error</td>
                <td>Request validation failed</td>
                <td>Check parameter types and values</td>
              </tr>
              <tr>
                <td><code>500</code></td>
                <td>Server Error</td>
                <td>Internal server error</td>
                <td>Retry request or contact support</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>Validation Error Response (422)</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`{
  "detail": [
    {
      "loc": ["body", "user_id"],
      "msg": "field required",
      "type": "value_error.missing"
    }
  ]
}`}
          </SyntaxHighlighter>
        </div>

        <div className="feature-highlight-box">
          <h4>💡 Best Practices</h4>
          <ul>
            <li><strong>Always check status codes:</strong> Implement proper error handling for all API responses</li>
            <li><strong>Validate parameters:</strong> Ensure required fields are provided and types are correct</li>
            <li><strong>Handle async tasks:</strong> Poll task status endpoint for memorization completion</li>
            <li><strong>Use appropriate endpoints:</strong> Choose between memory items and summaries based on your needs</li>
          </ul>
        </div>
      </div>

    </div>
  );
};

export default MemoryApis;